import { FeedbackData, SimulationSettings, CustomerProfile, SavedSession } from '../types';

/**
 * Downloads a file to the user's computer
 */
export function downloadFile(content: string, filename: string, mimeType: string): void {
    const blob = new Blob([content], { type: mimeType });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
}

/**
 * Generates a human-readable TXT format of the feedback
 */
export function generateFeedbackTXT(
    feedbackData: FeedbackData,
    settings: SimulationSettings,
    profile: CustomerProfile,
    timingDetails: string
): string {
    const timestamp = new Date().toLocaleString();

    let txt = `CUSTOMER SERVICE SIMULATION FEEDBACK REPORT\n`;
    txt += `===========================================\n\n`;

    txt += `Generated: ${timestamp}\n\n`;

    txt += `SIMULATION DETAILS\n`;
    txt += `------------------\n`;
    txt += `Channel: ${settings.channel}\n`;
    txt += `Industry: ${settings.industry}\n`;
    txt += `User Team: ${settings.userTeam || 'N/A'}\n`;
    txt += `Training Phase: ${settings.trainingPhase || 'N/A'}\n`;
    txt += `Scenario: ${settings.scenarioType}\n`;
    txt += `Knowledge Base: ${settings.knowledgeBaseTitle || (settings.knowledgeBaseText ? 'Custom KB' : 'None')}\n`;
    txt += `Customer Persona: ${settings.customerPersona || 'N/A'}\n`;
    txt += `Conversation Guide: ${settings.conversationGuideDisabled ? 'Disabled' : 'Enabled'}\n`;
    txt += `Customer Name: ${profile.name}\n`;
    txt += `Language: ${settings.language}\n`;
    txt += `Duration: ${timingDetails}\n\n`;

    txt += `OVERALL PERFORMANCE\n`;
    txt += `-------------------\n`;
    txt += `Score: ${feedbackData.overall_score}/100\n`;
    txt += `Summary: ${feedbackData.overall_summary}\n\n`;

    txt += `DETAILED CATEGORY FEEDBACK\n`;
    txt += `===========================\n\n`;

    Object.entries(feedbackData.feedback).forEach(([key, detail]) => {
        const categoryTitle = feedbackData.category_labels?.[key] || key.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());

        txt += `${categoryTitle}\n`;
        txt += `-`.repeat(categoryTitle.length) + `\n`;
        txt += `Score: ${detail.not_applicable ? 'N/A' : `${detail.score}/${detail.max_score}`}\n\n`;

        if (!detail.not_applicable) {
            if (detail.explanation_for_deduction) {
                txt += `Scoring Rationale:\n${detail.explanation_for_deduction}\n\n`;
            }

            if (detail.positive && detail.positive.length > 0) {
                txt += `Strengths:\n`;
                detail.positive.forEach(p => txt += `  ✓ ${p}\n`);
                txt += `\n`;
            }

            if (detail.improvement && detail.improvement.length > 0) {
                txt += `Areas for Improvement:\n`;
                detail.improvement.forEach(imp => txt += `  • ${imp}\n`);
                txt += `\n`;
            }

            if (detail.recommended_scripts && detail.recommended_scripts.length > 0) {
                txt += `Recommended Phrases:\n`;
                detail.recommended_scripts.forEach(script => txt += `  → "${script}"\n`);
                txt += `\n`;
            }

            if (detail.estimated_time_spent_seconds) {
                txt += `Time Spent: ${Math.round(detail.estimated_time_spent_seconds)} seconds\n\n`;
            }
        } else {
            txt += `This category was not applicable to this simulation.\n\n`;
        }
    });

    if (feedbackData.timing_analysis_summary) {
        txt += `TIMING ANALYSIS\n`;
        txt += `===============\n`;
        txt += `${feedbackData.timing_analysis_summary}\n\n`;
    }

    txt += `\n--- End of Report ---\n`;

    return txt;
}

/**
 * Generates a CSV format of the feedback for spreadsheet analysis
 */
export function generateFeedbackCSV(
    feedbackData: FeedbackData,
    settings: SimulationSettings,
    profile: CustomerProfile,
    timingDetails: string
): string {
    const escapeCSV = (str: string | number | null | undefined): string => {
        if (str === null || str === undefined) return '';
        const stringValue = String(str);
        // If content has comma, newline, or double quote, wrap in quotes and escape internal quotes
        if (/[",\n\r]/.test(stringValue)) {
            return `"${stringValue.replace(/"/g, '""')}"`;
        }
        return stringValue;
    };

    const joinArray = (arr: string[] | undefined): string => {
        if (!arr || arr.length === 0) return '';
        return arr.join(' | ');
    };

    // 1. Metadata Headers
    const headers = [
        "Timestamp",
        "User Name",
        "User Team",
        "Training Phase",
        "Channel",
        "Industry",
        "Scenario",
        "Knowledge Base",
        "Customer Persona",
        "Conversation Guide",
        "Customer Name",
        "Language",
        "Duration",
        "Overall Score",
        "Overall Summary",
        "Timing Analysis"
    ];

    // 2. Metadata Values
    const rowValues: any[] = [
        new Date().toISOString(),
        settings.userName,
        settings.userTeam || '',
        settings.trainingPhase || '',
        settings.channel,
        settings.industry,
        settings.scenarioType,
        settings.knowledgeBaseTitle || (settings.knowledgeBaseText ? 'Custom KB' : 'None'),
        settings.customerPersona || 'N/A',
        settings.conversationGuideDisabled ? 'Disabled' : 'Enabled',
        profile.name,
        settings.language,
        timingDetails,
        feedbackData.overall_score,
        feedbackData.overall_summary,
        feedbackData.timing_analysis_summary || ''
    ];

    // 3. Dynamic Category Columns
    const sortedCategoryKeys = Object.keys(feedbackData.feedback).sort();

    sortedCategoryKeys.forEach(key => {
        const detail = feedbackData.feedback[key];
        const categoryTitle = key.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());

        headers.push(`${categoryTitle} Score`);
        headers.push(`${categoryTitle} Max`);
        headers.push(`${categoryTitle} Notes (Strengths|Improvements)`);

        if (detail.not_applicable) {
            rowValues.push("N/A");
            rowValues.push(detail.max_score);
            rowValues.push("Not Applicable");
        } else {
            rowValues.push(detail.score);
            rowValues.push(detail.max_score);

            const combinedNotesParts = [];
            if (detail.positive?.length) combinedNotesParts.push(`STRENGTHS: ${joinArray(detail.positive)}`);
            if (detail.improvement?.length) combinedNotesParts.push(`IMPROVEMENTS: ${joinArray(detail.improvement)}`);
            if (detail.recommended_scripts?.length) combinedNotesParts.push(`RECOMMENDED: ${joinArray(detail.recommended_scripts)}`);
            if (detail.explanation_for_deduction) combinedNotesParts.push(`NOTE: ${detail.explanation_for_deduction}`);

            rowValues.push(combinedNotesParts.join('\n'));
        }
    });

    // 4. Construct CSV
    const headerRow = headers.map(h => escapeCSV(h)).join(',');
    const dataRow = rowValues.map(v => escapeCSV(v)).join(',');

    return `${headerRow}\n${dataRow}`;
}

/**
 * Downloads both TXT and CSV feedback files
 */
export function downloadFeedbackFiles(
    feedbackData: FeedbackData,
    settings: SimulationSettings,
    profile: CustomerProfile,
    timingDetails: string
): void {
    const timestamp = new Date().toISOString().replace(/[:.]/g, '-').slice(0, 19);

    // Generate and download TXT file
    const txtContent = generateFeedbackTXT(feedbackData, settings, profile, timingDetails);
    downloadFile(txtContent, `feedback_${timestamp}.txt`, 'text/plain');

    // Small delay to ensure both downloads work
    setTimeout(() => {
        // Generate and download CSV file
        const csvContent = generateFeedbackCSV(feedbackData, settings, profile, timingDetails);
        downloadFile(csvContent, `feedback_${timestamp}.csv`, 'text/csv');
    }, 100);
}

/**
 * Generates a batch CSV for multiple sessions.
 * Includes: session metadata, overall score, interaction summary, timing analysis,
 * and per-category score, positive points, improvements, and feedback explanation.
 */
export function generateBatchFeedbackCSV(sessions: SavedSession[]): string {
    if (sessions.length === 0) return "";

    // Always wrap every field in quotes to safely handle commas, newlines, and quotes
    const escapeCSV = (str: any): string => {
        if (str === null || str === undefined) return '""';
        return `"${String(str).replace(/"/g, '""')}"`;
    };

    const cleanDuration = (val: any): string => {
        if (!val) return "00:00";
        const str = String(val);
        // Remove "Total Time: " or similar
        let clean = str.replace(/[^\d:]/g, ' ').trim();
        const m = clean.match(/(\d+):(\d+)(?::(\d+))?/);
        if (!m) return "00:00";
        
        let mins = 0;
        let secs = 0;
        
        if (m[3]) {
            // H:M:S
            const h = parseInt(m[1], 10);
            const mn = parseInt(m[2], 10);
            const sc = parseInt(m[3], 10);
            mins = h * 60 + mn;
            secs = sc;
        } else {
            // M:S
            mins = parseInt(m[1], 10);
            secs = parseInt(m[2], 10);
        }
        
        return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
    };

    const parseScore = (v: any): number => {
        if (v === null || v === undefined) return 0;
        if (typeof v === 'number') return v;
        const str = String(v).trim();
        const match = str.match(/-?\d+(\.\d+)?/);
        return match ? parseFloat(match[0]) : 0;
    };

    const headers = [
        "Session ID",
        "Timestamp",
        "Agent Name",
        "Agent Email",
        "Agent Team",
        "Training Phase",
        "Channel",
        "Industry",
        "Scenario",
        "Knowledge Base",
        "Customer Persona",
        "Conversation Guide",
        "Duration",
        "Overall Score",
        "Interaction Summary",
        "Timing Analysis"
    ];

    // Collect all unique category keys across every session for consistent columns
    const categoryKeysSet = new Set<string>();
    sessions.forEach(s => {
        if (s.feedback?.feedback) {
            Object.keys(s.feedback.feedback).forEach(k => categoryKeysSet.add(k));
        }
    });
    const sortedCategories = Array.from(categoryKeysSet).sort();

    sortedCategories.forEach(cat => {
        const title = cat.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
        headers.push(`${title} Score`);
        headers.push(`${title} - Positive`);
        headers.push(`${title} - Improvements`);
        headers.push(`${title} - Feedback`);
    });

    const rows = [headers.map(h => escapeCSV(h)).join(',')];

    sessions.forEach(s => {
        const date = s.timestamp?.toDate
            ? s.timestamp.toDate().toISOString()
            : (s.timestamp ? String(s.timestamp) : "N/A");

        const row: any[] = [
            s.id,
            date,
            s.userName || s.settings?.userName || "N/A",
            s.userEmail || "N/A",
            (s as any).userTeam || s.settings?.userTeam || "N/A",
            s.settings?.trainingPhase || "N/A",
            s.settings?.channel || "N/A",
            s.settings?.industry || "N/A",
            s.settings?.scenarioType || "N/A",
            s.settings?.knowledgeBaseTitle || (s.settings?.knowledgeBaseText ? "Custom KB" : "None"),
            s.settings?.customerPersona || "N/A",
            s.settings?.conversationGuideDisabled ? "Disabled" : "Enabled",
            cleanDuration(s.duration),
            parseScore(s.feedback?.overall_score),
            s.feedback?.overall_summary || "",
            s.feedback?.timing_analysis_summary || ""
        ];

        sortedCategories.forEach(cat => {
            const detail = s.feedback?.feedback?.[cat];
            if (!detail) {
                row.push("", "", "", "");
                return;
            }
            if (detail.not_applicable) {
                row.push("N/A", "", "", "");
                return;
            }
            row.push(detail.score ?? "");
            row.push((detail.positive || []).join(' | '));
            row.push((detail.improvement || []).join(' | '));
            row.push(detail.explanation_for_deduction || "");
        });

        rows.push(row.map(r => escapeCSV(r)).join(','));
    });

    return rows.join('\n');
}
